/**
 * usePrint - Hook for print functionality
 *
 * Provides methods for printing and generating PDFs of the Closing Disclosure.
 */

import * as React from 'react';

export interface UsePrintOptions {
  /** Title for the print document */
  documentTitle?: string;
  /** Callback before print */
  onBeforePrint?: () => void;
  /** Callback after print */
  onAfterPrint?: () => void;
}

export interface UsePrintReturn {
  /** Trigger browser print dialog */
  print: () => void;
  /** Whether print is in progress */
  isPrinting: boolean;
  /** Open print preview in new window */
  openPrintPreview: () => void;
  /** Generate PDF using browser's print-to-PDF (opens print dialog) */
  generatePDF: () => void;
}

/**
 * Hook for printing the Closing Disclosure
 */
export function usePrint(options: UsePrintOptions = {}): UsePrintReturn {
  const { documentTitle, onBeforePrint, onAfterPrint } = options;
  const [isPrinting, setIsPrinting] = React.useState(false);

  // Handle before/after print events
  React.useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrinting(true);
      onBeforePrint?.();
    };

    const handleAfterPrint = () => {
      setIsPrinting(false);
      onAfterPrint?.();
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [onBeforePrint, onAfterPrint]);

  /**
   * Trigger the browser print dialog
   */
  const print = React.useCallback(() => {
    // Set document title for print
    const originalTitle = document.title;
    if (documentTitle) {
      document.title = documentTitle;
    }

    // Trigger print
    window.print();

    // Restore title after a short delay
    if (documentTitle) {
      setTimeout(() => {
        document.title = originalTitle;
      }, 100);
    }
  }, [documentTitle]);

  /**
   * Open print preview in a new window
   * This creates a minimal window with just the CD content for printing
   */
  const openPrintPreview = React.useCallback(() => {
    // Find the CD content
    const cdContent = document.querySelector('.closing-disclosure, .cd-print-preview-content');
    if (!cdContent) {
      console.warn('No Closing Disclosure content found to preview');
      return;
    }

    // Get stylesheets
    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return sheet.href
            ? `<link rel="stylesheet" href="${sheet.href}">`
            : `<style>${Array.from(sheet.cssRules)
                .map((rule) => rule.cssText)
                .join('\n')}</style>`;
        } catch {
          // Cross-origin stylesheets will throw
          return sheet.href ? `<link rel="stylesheet" href="${sheet.href}">` : '';
        }
      })
      .join('\n');

    // Create print window
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      console.warn('Could not open print preview window. Check popup blocker settings.');
      return;
    }

    // Write content to print window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${documentTitle || 'Closing Disclosure - Print Preview'}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          ${styles}
          <style>
            body {
              margin: 0;
              padding: 20px;
              background: #525659;
            }
            .print-preview-wrapper {
              max-width: 900px;
              margin: 0 auto;
            }
            .print-preview-toolbar {
              background: white;
              padding: 16px 24px;
              margin-bottom: 20px;
              border-radius: 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .print-preview-toolbar h1 {
              margin: 0;
              font-size: 18px;
              font-weight: 600;
            }
            .print-preview-toolbar button {
              padding: 8px 16px;
              font-size: 14px;
              cursor: pointer;
              border-radius: 4px;
              margin-left: 8px;
            }
            .print-preview-toolbar .btn-print {
              background: #1976D2;
              color: white;
              border: none;
            }
            .print-preview-toolbar .btn-close {
              background: white;
              color: #333;
              border: 1px solid #ddd;
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
                background: white;
              }
              .print-preview-wrapper {
                max-width: none;
              }
              .print-preview-toolbar {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-preview-wrapper">
            <div class="print-preview-toolbar">
              <h1>Closing Disclosure - Print Preview</h1>
              <div>
                <button class="btn-close" onclick="window.close()">Close</button>
                <button class="btn-print" onclick="window.print()">Print</button>
              </div>
            </div>
            ${cdContent.outerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
  }, [documentTitle]);

  /**
   * Generate PDF - triggers print dialog where user can select "Save as PDF"
   */
  const generatePDF = React.useCallback(() => {
    print();
  }, [print]);

  return {
    print,
    isPrinting,
    openPrintPreview,
    generatePDF,
  };
}

/**
 * Hook to detect if currently in print mode
 */
export function useIsPrinting(): boolean {
  const [isPrinting, setIsPrinting] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('print');

    const handleChange = (e: MediaQueryListEvent) => {
      setIsPrinting(e.matches);
    };

    // Check initial state
    setIsPrinting(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return isPrinting;
}
